<script lang="ts" setup>import { toRef as _toRef, ref as _ref } from 'vue';

/**
 * A dialog component that responsively renders as an action sheet on mobile devices
 * (slides up from bottom) and as a standard dialog modal on desktop.
 */
import {
  Dialog,
  DialogPanel,
  DialogTitle,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'
import { trackClick, trackEvent, trackModal } from '@shared/tracking'
import { pushFlashTarget, removeFlashTarget } from '@shared/stores/flash'
import { templateRef, whenever } from '@vueuse/core'
import { modalDropdown } from '@consumer/utils/transitions'
import { useListeners } from '@shared/vue'
import { isFunction } from '@shared/functions'
import { isMobile } from '@consumer/utils/breakpoints'
import { useVisualViewport } from '@shared/composables'
import { savedScrollPositionForDialog } from '@consumer/services/modals'
import type { AllowedComponentProps, Component } from 'vue'

defineOptions({
  inheritAttrs: false,
})

withDefaults(defineProps<{
  name: string
  id?: string
  as?: Component // Wrapper component for dialog content (e.g., GStepper, GForm)
  asProps?: Record<string, any> // Props to pass to the wrapper component
  appear?: boolean
  persistent?: boolean // If true, modal cannot be dismissed by clicking outside or hitting ESC
  fullscreen?: boolean // If true, modal is fullscreen on both desktop and mobile
  hideCloseButton?: boolean
  disableClose?: boolean
  noPadding?: boolean
  centered?: boolean
  width?: 'narrow' | 'wide' | 'mediumwide' | 'superwide' | 'ultrawide' | 'unconstrained'
  headerClass?: AllowedComponentProps['class']
  onBack?: (closeModal?: () => void) => void
  showBackButton?: boolean
  floatingFooter?: boolean
  noFlashTarget?: boolean // If true, dialog will not register a flash teleport target
}>(), { persistent: false,fullscreen: false,noPadding: false,centered: true,width: 'unconstrained',floatingFooter: false,noFlashTarget: false, })

let open = (defineModel<boolean>('open', { default: undefined }))

if (open.value === undefined) {
  open.value = false
  // Setting open to true on the next tick to allow for transition animations
  setTimeout(() => {
    open.value = true
  }, 0)
}

const hasCloseListener = useListeners(listeners => isFunction(listeners.onClose))

const emit = defineEmits<{ close: [closeModal: Function]; remove: [] }>()

const contentWrapper = templateRef<HTMLElement>('contentWrapper')
const __$temp_1 = (useVisualViewport()),
  keyboardHeight = _toRef(__$temp_1, 'keyboardHeight'),
  isKeyboardOpen = _toRef(__$temp_1, 'isKeyboardOpen'),
  viewportOffsetTop = _toRef(__$temp_1, 'viewportOffsetTop');

// Emit an event to notify that the user intends to close the modal.
//
// NOTE: When the parent component adds a @close listener, it must use the
// callback to manually close the modal.
function stopAndCloseModal () {
  if (hasCloseListener) {
    emit('close', () => { open.value = false })
  }
  else {
    open.value = false
  }
}

// 'reset' is emitted by form elements when GCancelButton is clicked.
//
// NOTE: By leveraging the native 'reset' event, there is no need to manually
// add a close listener on every modal forms.
function onFormReset () {
  stopAndCloseModal()
}

function closeModal () {
  trackEvent('click', `close-modal-button-${__props.name}`)
  stopAndCloseModal()
}

function onClickOutside () {
  if (__props.persistent || __props.disableClose) return

  trackEvent('click', `close-modal-outside-${__props.name}`)
  stopAndCloseModal()
}

function scrollTop () {
  if (contentWrapper.value) {
    contentWrapper.value.scrollTop = 0
  }
}

const slots = useSlots()

// Mobile always uses action sheet style (slides up from bottom)
const isActionSheet = computed(() => isMobile.value)

// Action sheet transition (slide up from bottom)
const actionSheetTransition = {
  enter: 'transition ease-in-out duration-300 transform',
  enterFrom: 'translate-y-full',
  enterTo: 'translate-y-0',
  leave: 'transition ease-in-out duration-300 transform',
  leaveFrom: 'translate-y-0',
  leaveTo: 'translate-y-full',
}

whenever(() => open.value, () => trackModal(__props.name), { immediate: true })

// Prevent HeadlessUI from resetting scroll position when it sets overflow:hidden on html
onMounted(() => {
  // Capture current scroll position; fall back to the position saved by modals.ts
  // for async-loaded modals where mount happens after scroll may have shifted.
  const savedScrollY = window.scrollY || savedScrollPositionForDialog

  const observer = new MutationObserver(() => {
    if (document.documentElement.style.overflow === 'hidden') {
      document.documentElement.style.overflow = ''
      window.scrollTo({ top: savedScrollY, behavior: 'instant' })
    }
  })

  observer.observe(document.documentElement, {
    attributes: true,
    attributeFilter: ['style'],
  })

  // Disconnect after dialog transition completes
  setTimeout(() => observer.disconnect(), 500)
})

const wrapperRef = _ref<any>(null)

// Register a teleport target so GFlashManager can render notifications inside this dialog.
const flashTarget = templateRef<HTMLElement>('flashTarget')

watch(flashTarget, (el, oldEl) => {
  if (__props.noFlashTarget) return
  if (oldEl) removeFlashTarget(oldEl)
  if (el) pushFlashTarget({ el })
})

onBeforeUnmount(() => {
  if (__props.noFlashTarget) return
  if (flashTarget.value) removeFlashTarget(flashTarget.value)
})

defineExpose({
  scrollTop,
  get wrapperRef () { return wrapperRef.value },
})
</script>

<template>
  <TransitionRoot as="template" :show="open" :appear="appear" @afterLeave="emit('remove')">
    <Dialog
      :id="id"
      as="div"
      class="g-dialog fixed inset-0 z-dialog"
      :style="isActionSheet && isKeyboardOpen ?
        `bottom: ${Math.max(0, keyboardHeight - viewportOffsetTop)}px` : undefined"
      :class="[
        isActionSheet
          ? 'flex items-end'
          : [
            'blur-area overflow-y-auto dialog break-word grid justify-center',
            fullscreen ? 'p-0' : 'py-8',
            {
              'content-center': centered && !fullscreen,
              'content-start': !centered && !fullscreen,
            },
          ],
      ]"
      :static="true"
      @close="onClickOutside"
    >
      <!-- Backdrop -->
      <TransitionChild
        as="template"
        :enter="isActionSheet ? 'transition-opacity ease-in-out duration-500' : 'ease-out duration-200'"
        enterFrom="opacity-0"
        enterTo="opacity-100"
        :leave="isActionSheet ? 'transition-opacity ease-in-out duration-700' : 'ease-in duration-200'"
        leaveFrom="opacity-100"
        leaveTo="opacity-0"
      >
        <div class="fixed inset-0 bg-black bg-opacity-40" @click.stop/>
      </TransitionChild>

      <!-- Panel -->
      <TransitionChild
        as="template"
        :appear="appear"
        v-bind="isActionSheet ? actionSheetTransition : modalDropdown"
      >
        <DialogPanel
          as="div"
          class="g-dialog-panel relative bg-white flex flex-col overflow-hidden"
          :class="[
            isActionSheet
              ? ['w-full', fullscreen ? 'h-full' : 'rounded-t-v2lg']
              : fullscreen
                ? 'w-screen h-screen max-w-none rounded-none border-none'
                : [
                  'mx-auto border-solid border-1 border-grey-primary justify-evenly',
                  'rounded-v2lg lg:max-w-screen-lg max-w-440px',
                  width,
                  noPadding && 'rounded-v2lg',
                ],
          ]"
          :style="isActionSheet && !fullscreen ?
            `max-height: calc(100svh - 1rem${isKeyboardOpen ? ` - ${keyboardHeight}px` : ''});` : undefined"
          v-bind="$attrs"
          aria-hidden="false"
          aria-modal="true"
          @click="trackClick"
          @reset="onFormReset"
        >
          <component :is="as ?? 'div'" ref="wrapperRef" v-bind="asProps" class="flex flex-col flex-1 overflow-hidden">
            <!-- Header -->
            <GSheetHeader
              v-if="slots.header || !hideCloseButton"
              :hasTitle="!!slots.header"
              :hideCloseButton="hideCloseButton"
              :disableClose="disableClose"
              :showBackButton="!!onBack && (showBackButton ?? isActionSheet)"
              :class="headerClass"
              @close="closeModal"
              @back="() => onBack && onBack(closeModal)"
            >
              <DialogTitle v-if="slots.header" as="div" class="w-full">
                <slot name="header"/>
              </DialogTitle>
            </GSheetHeader>

            <!-- Content (scrollable) -->
            <div
              ref="contentWrapper"
              class="flex-1 overflow-y-auto g-dialog-content"
              :class="[
                isActionSheet
                  ? ['px-5', { 'pt-16': !slots.header }, { '!p-0': noPadding }]
                  : ['px-5', { 'pt-16': !slots.header }, { '!p-0': noPadding }],
              ]"
            >
              <slot/>
            </div>

            <!-- Flash messages teleport target -->
            <div ref="flashTarget" class="shrink-0"/>

            <!-- Footer -->
            <div
              v-if="slots.footer"
              class="g-sheet-dialog-footer shrink-0 p-6"
              :class="{'absolute bottom-0 left-0 w-full': floatingFooter}"
            >
              <slot name="footer"/>
            </div>

            <button class="opacity-0 absolute bottom-0"/>
          </component>
        </DialogPanel>
      </TransitionChild>
    </Dialog>
  </TransitionRoot>
</template>

<style lang="scss">
.g-dialog {
  scrollbar-width: none; /* Hide scrollbar on overlay — content scrolls inside the panel */

  &::-webkit-scrollbar {
    display: none;
  }

  .g-dialog-panel {
    box-shadow: 0 4px 20px rgb(0 0 0 / 15%);
    max-width: 100vw;
  }

  .g-button-large:not(.g-button-no-label):not(.g-button-round) {
    width: 100%;
  }
}

.narrow {
  width: 350px;
}

.wide {
  width: 398px;
}

.mediumwide {
  width: 495px;
}

.superwide {
  width: 600px;
}

.ultrawide {
  width: 800px;
}

/* Remove focus rings from buttons inside dialogs */
.g-dialog .g-button:focus,
.g-dialog .g-sheet-button:focus {
  box-shadow: none;
  outline: none;
}

#close-modal-button:focus:not(:hover, :active) {
  --color-focus: var(--color);
  --bg-focus: var(--bg);
}

.g-sheet-dialog-title {
  font-size: 32px;

  @media screen and (min-width: 1024px) {
    font-size: 34px;
  }
}
</style>
